import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import ContentInfusionsSlider from '../Generic/ContentInfusionsSlider';
import SliderTile, { SliderTileItem } from '../Generic/ContentInfusionsSlider/SliderTile';
import { looksSliderRatios } from 'Client/constants/aspectRatios';
import useBellaTranslator from 'Client/hooks/useTranslator';
import StateInterface from 'Client/redux/types';
import useGa from 'Client/hooks/useGa';
import { getLooksUrl } from 'Client/urlService';
import { LookSliderItem } from 'Client/redux/looksSlider/types';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import GenericImpressionWrapper from 'Client/components/Generic/GenericImpressionWrapper';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

const LooksSlider: React.FC = () => {
    const t = useBellaTranslator();
    const gaTracking = useGa();
    const { p100IncreasedDesktopPageWidth } = useFeatureToggle();
    const dispatch = useDispatch();
    const looksSliderData =
        useSelector<StateInterface, LookSliderItem[] | undefined>(state => state.looksSlider.looks) || [];

    if (looksSliderData.length === 0) {
        return null;
    }

    const handleCardClick = (look: LookSliderItem, trackingName: string) => {
        gaTracking.homepage.trackLooksSliderTrackingClick(trackingName);
        dispatch(setLDPKnownInfoAction(look));
    };

    const handleBannerImpression = (trackingName: string) => {
        gaTracking.homepage.trackBannerImpression(trackingName);
    };

    const mapToCards = (look: LookSliderItem, i: number): SliderTileItem => {
        const trackingName = `${look.title} ${i + 1}/${looksSliderData.length}`;
        return {
            id: look.urlKey,
            targetUrl: look.url,
            fallbackSrc: look.imgUrl,
            desktopSrc: look.imgUrl,
            mobileSrc: look.imgUrl,
            title: look.title,
            ctaText: t('Read and Shop'),
            onClick: () => handleCardClick(look, trackingName),
            trackImpressionFunction: () => handleBannerImpression(trackingName),
        };
    };

    const cards = looksSliderData.map(mapToCards);

    const ctaOptions = {
        text: t('See all'),
        link: getLooksUrl(),
        trackingFn: () => gaTracking.homepage.trackLooksSliderSeeAllTrackingClick(),
    };

    const sliderTrackLabel = 'Shop_the_Look';
    const trackArrowClicks = () => {
        if (isDesktop()) {
            gaTracking.homepage.trackSliderArrowComponentClick(sliderTrackLabel);
        }
    };

    return (
        <GenericImpressionWrapper
            action={() => gaTracking.homepage.trackSliderImpression('Shop the Look slider impression')}
        >
            <ContentInfusionsSlider
                title={t('Shop the Look')}
                onArrowClicks={trackArrowClicks}
                ctaOptions={ctaOptions}
                showBorder={false}
                noSideMarginsForWideScreen={false}
            >
                {cards.map(look => (
                    <SliderTile
                        ratio={looksSliderRatios}
                        card={look}
                        key={look.title}
                        p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                    />
                ))}
            </ContentInfusionsSlider>
        </GenericImpressionWrapper>
    );
};

export default LooksSlider;
